import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { LocationQuery, LocationQueryValue } from 'vue-router';
import { createSharedComposable } from '@vueuse/core';
import { AgentType } from '../constants/enum';

const VALID_AGENT_TYPES = new Set<string>(Object.values(AgentType));
const DEFAULT_TAB = AgentType.DISCLOSURE;
const STANDARD_PROSECUTION_MODE = 'standard';

function getFirstQueryValue(value: LocationQueryValue | LocationQueryValue[] | undefined): string | undefined {
    if (Array.isArray(value)) return value[0] || undefined;
    return value || undefined;
}

export function getTabFromQuery(
    agent: LocationQueryValue | LocationQueryValue[] | undefined,
    prosecutionMode?: LocationQueryValue | LocationQueryValue[],
): AgentType {
    const normalizedAgent = getFirstQueryValue(agent);
    const normalizedProsecutionMode = getFirstQueryValue(prosecutionMode);
    if (
        normalizedAgent === AgentType.PATENT_DRAFT
        && normalizedProsecutionMode === STANDARD_PROSECUTION_MODE
    ) {
        return AgentType.PROSECUTION;
    }
    if (normalizedAgent && VALID_AGENT_TYPES.has(normalizedAgent)) return normalizedAgent as AgentType;
    return DEFAULT_TAB;
}

function createTabQuery(query: LocationQuery, tab: AgentType): LocationQuery {
    const nextQuery = { ...query };
    delete nextQuery.prosecution_mode;
    return { ...nextQuery, agent: tab };
}

export const useAgentTabs = createSharedComposable(() => {
    const route = useRoute();
    const router = useRouter();

    const activeTab = ref<AgentType>(getTabFromQuery(route.query.agent, route.query.prosecution_mode));

    watch(() => [route.query.agent, route.query.prosecution_mode], ([newAgent, newProsecutionMode]) => {
        activeTab.value = getTabFromQuery(newAgent, newProsecutionMode);
    });

    function setActiveTab(tab: AgentType) {
        activeTab.value = tab;
        router.push({ query: createTabQuery(route.query, tab) });
    }

    return { activeTab, setActiveTab };
});
